import type { JSX } from 'react';
import { useState } from 'react';
import { Anchor, Card, Center, Group, Image, Loader, Select, SimpleGrid, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { objectsApi, type BenchmarkObject } from '../../api';
import { formatAmount } from '../../format';
import { typesWithoutBenchmarks } from './benchmarkObjects';

/**
 * Example objects of one building type, with photo and key figures (AP-88):
 * what the catalogue's values stand for. Folded to a line until asked for.
 */
export function BenchmarkObjects({ buildingType, open: startOpen = false }: { buildingType: string; open?: boolean }): JSX.Element | null {
  const types = useQuery({ queryKey: ['benchmark-objects', 'types'], queryFn: objectsApi.types, staleTime: 3_600_000 });
  const count = types.data?.find((t) => t.buildingType === buildingType)?.objects ?? 0;
  const [open, setOpen] = useState(startOpen);
  const list = useQuery({ queryKey: ['benchmark-objects', buildingType], queryFn: () => objectsApi.list(buildingType), enabled: open && count > 0 });
  if (count === 0) return null;
  return (
    <Stack gap="xs">
      <Anchor size="sm" component="button" type="button" onClick={() => setOpen((v) => !v)}>
        {open ? '▾' : '▸'} {count} Beispielobjekte zu „{buildingType}“
      </Anchor>
      {open && list.isPending && <Center h={80}><Loader size="sm" /></Center>}
      {open && list.data && (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, xl: 4 }} spacing="sm">
          {list.data.map((o) => <ObjectCard key={o.objectNo} o={o} />)}
        </SimpleGrid>
      )}
    </Stack>
  );
}

/**
 * Example objects of building types without a benchmark (AP-129): neither the
 * filter nor the popup of a row reaches them, e.g. after reading in a new volume.
 */
export function ObjectsWithoutBenchmarks({ benchmarkTypes }: { benchmarkTypes: readonly (string | null)[] }): JSX.Element | null {
  const types = useQuery({ queryKey: ['benchmark-objects', 'types'], queryFn: objectsApi.types, staleTime: 3_600_000 });
  const [type, setType] = useState<string | null>(null);
  const others = typesWithoutBenchmarks(types.data ?? [], benchmarkTypes);
  if (others.length === 0) return null;
  return (
    <Stack gap="xs">
      <Select
        size="xs" w={320} clearable searchable comboboxProps={{ withinPortal: true }}
        label="Beispielobjekte ohne Kennwerte"
        placeholder={`${others.length} Gebäudearten`}
        data={others.map((t) => ({ value: t.buildingType, label: `${t.buildingType} (${t.objects})` }))}
        value={type} onChange={setType}
      />
      {type && <BenchmarkObjects key={type} buildingType={type} open />}
    </Stack>
  );
}

const FACTS = ['Standard', 'Land', 'Kreis', 'Bauzeit', 'Kennwerte', 'Hinweis'] as const;

function ObjectCard({ o }: { o: BenchmarkObject }): JSX.Element {
  const cost = o.facts['€/m² BGF'];
  const area = (k: string, u: string): string | null => (typeof o.facts[k] === 'number' ? `${k} ${formatAmount(o.facts[k] as number, 0)} ${u}` : null);
  return (
    <Card withBorder padding="xs" radius="md">
      {o.image && (
        <Card.Section>
          <Image src={objectsApi.imageUrl(o.objectNo)} alt={o.title} h={150} fit="cover" loading="lazy" />
        </Card.Section>
      )}
      <Stack gap={2} mt="xs">
        <Group justify="space-between" gap={4} wrap="nowrap" align="flex-start">
          <Text size="sm" fw={600} lineClamp={2}>{o.title}</Text>
          <Text size="xs" ff="monospace" c="dimmed">{o.objectNo}</Text>
        </Group>
        {typeof cost === 'number' && <Text size="sm" fw={700}>{formatAmount(cost, 0)} €/m² BGF</Text>}
        <Text size="xs" c="dimmed">{[area('BGF', 'm²'), area('NUF', 'm²'), area('BRI', 'm³')].filter(Boolean).join(' · ')}</Text>
        {o.description && <Text size="xs" lineClamp={4} title={o.description}>{o.description}</Text>}
        <Text size="xs" c="dimmed">{FACTS.filter((k) => o.facts[k]).map((k) => `${k}: ${o.facts[k]}`).join(' · ')}</Text>
        {o.planner && <Text size="xs" c="dimmed">Planung: {o.planner}</Text>}
        <Text size="xs" c="dimmed">{[o.credit, o.page ? `${o.edition}, S. ${o.page}` : o.edition].filter(Boolean).join(' · ')}</Text>
      </Stack>
    </Card>
  );
}
